import { useApplicationModals } from "app/common/hooks";
import { Button } from "app/components/atoms/button";
import { ModalMolecule } from "app/components/molecules/modal";
import { Input } from "app/components/atoms/input";
import api from "app/integration/api";
import { useEffect, useState } from "react";
import * as XLSX from 'xlsx';

// Format date to YYYY-MM-DD
const formatDate = (date) => {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

// Format date to Indonesian locale
const formatDateLocale = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get date one month ago
const getOneMonthAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDate(date);
};

function ExportReturSupplierExcel({ onSuccess = () => {} }) {
  const { showStates, closeModal } = useApplicationModals();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  
  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: getOneMonthAgo(),
    endDate: formatDate(new Date())
  });
  const [dateError, setDateError] = useState('');

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const returSupplierResponse = await api.returSupplier.index();
      const variantsResponse = await api.productVariant.index();
      
      setFilteredData(returSupplierResponse.data.filter(retur => {
        const returDate = new Date(retur.createdAt);
        const endDate = new Date(dateRange.endDate);
        endDate.setDate(endDate.getDate() + 1); // Add 1 day to end date
        return returDate >= new Date(dateRange.startDate) && 
               returDate <= endDate;
      }) || []);
      setProductVariants(variantsResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showStates["exportReturSupplierExcel"]) {
      setDateRange({
        startDate: getOneMonthAgo(),
        endDate: formatDate(new Date())
      });
      fetchData();
    }
  }, [showStates["exportReturSupplierExcel"]]);

  const handleClose = () => {
    closeModal("exportReturSupplierExcel");
    setDateError('');
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilter = () => {
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      setDateError('Tanggal mulai tidak boleh lebih besar dari tanggal akhir');
      return;
    }
    setDateError('');
    fetchData();
  };

  const formatForExport = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}${month}${year}`;
  };

  const exportToExcel = () => {
    const dataForExcel = filteredData.flatMap(retur => {
      const productVariant = productVariants.find(pv => pv.id === retur.product_variant_id);
      const productName = productVariant?.product_detail?.name || '-';
      const variantInfo = [
        productVariant?.color?.name,
        productVariant?.size?.name
      ].filter(Boolean).join(', ') || '-';

      if (retur?.product_code_list?.length > 0) {
        return retur.product_code_list.map(code => ({
          'No. Retur': retur.code || '-',
          'Tanggal': formatDateLocale(retur.createdAt),
          'Produk': productName,
          'Varian': variantInfo,
          'Kode Produk': code.qr_code || '-',
          'Alasan Retur': code.retur_reason || retur.reason || '-',
          'Keterangan': code.notes || '-',
          'Status': retur.status ? retur.status.charAt(0).toUpperCase() + retur.status.slice(1) : '-'
        }));
      }

      return {
        'No. Retur': retur.code || '-',
        'Tanggal': formatDateLocale(retur.createdAt),
        'Produk': productName,
        'Varian': variantInfo,
        'Kode Produk': '-',
        'Alasan Retur': retur.reason || '-',
        'Keterangan': '-',
        'Jumlah': retur.qty || 0,
        'Status': retur.status ? retur.status.charAt(0).toUpperCase() + retur.status.slice(1) : '-'
      };
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    
    const columnWidths = [
      { wch: 15 }, // No. Retur
      { wch: 20 }, // Tanggal
      { wch: 30 }, // Produk
      { wch: 20 }, // Varian
      { wch: 20 }, // Kode Produk
      { wch: 30 }, // Alasan Retur
      { wch: 30 }, // Keterangan
      { wch: 15 }, // Jumlah
      { wch: 15 }  // Status
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Retur Supplier");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    const start = formatForExport(dateRange.startDate);
    const end = formatForExport(dateRange.endDate);
    link.download = `laporan_retur_supplier_${start}_${end}.xlsx`;
    
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <ModalMolecule
      show={showStates["exportReturSupplierExcel"]}
      title="Export Laporan Retur Supplier"
      onClose={handleClose}
      size="lg"
    >
      <div className="space-y-4 p-4">
        {isLoading ? (
          <div className="text-center py-8">
            <p>Menyiapkan data...</p>
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h.01a1 1 0 100-2H10V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Data yang akan diexport: <span className="font-medium">{filteredData.length} data retur supplier</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Mulai</label>
                <Input testId="modal-export-retur-supplier-excel-startdate-input"
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Akhir</label>
                <Input testId="modal-export-retur-supplier-excel-enddate-input"
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  className="w-full"
                />
              </div>
              <div className="flex items-end">
                <Button testId="modal-export-retur-supplier-excel-terapkan-filter-button"
                  type="button"
                  onClick={handleApplyFilter}
                  className="w-full"
                >
                  Terapkan Filter
                </Button>
              </div>
              {dateError && (
                <div className="col-span-3 text-sm text-red-600">
                  {dateError}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button testId="modal-export-retur-supplier-excel-batal-button"
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Batal
              </Button>
              <Button testId="modal-export-retur-supplier-excel-export-button"
                type="button"
                onClick={exportToExcel}
                disabled={isLoading || filteredData.length === 0}
              >
                {isLoading ? 'Mengexport...' : 'Export Excel'}
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalMolecule>
  );
}

export default ExportReturSupplierExcel;